import {
  skipToken,
  UseMutationOptions,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { role } from "@/features/auth/types";
import { UploadOlkypassLvl2Response } from "@/features/user/document/types";
import { AddressType } from "@/features/user/types";
import { StandardResponse } from "@/features/utils/types";
import { DocumentAction } from "@/molecules/consent-document/ConsentDocument.react";
import { useAppSelector } from "@/redux/hooks";
import { getUserRoles } from "@/redux/slices/userSlice";
import { CompanyApiClient } from "../api-client/api-client";
import {
  authenticatedCompanyFormType,
  CheckJoinLegalEntityResponse,
  CompanyPaymentInformations,
  CompanyRelatedPersonDtoRequest,
  CompanyResponse,
  companyDetails,
  DeclarativeCompany,
  DeclarativeCompanyResponse,
  emailInvitSchema,
  GetLinkInfoRequest,
  InitCardTransactionRequest,
  InitCardTransactionResponse,
  JoinLegalEntityRequest,
  LegalEntityCreateSchema,
  LegalEntityFrontDto,
  LegalEntityLinkDto,
  ProVerifiedUploadDocumentBody,
  rcsGetPayloadRequest,
  rcsResponseType
} from "../types";
import { QueryOptionsFactory } from "../utils";
import { useCompanyContext } from "./use-company-context";
import { ApiError } from "@/utils/internal-code-handler";
export type useKYCProps = {
  address: AddressType;
};

export const useCompany = () => {
  const roles = useAppSelector(getUserRoles);
  const isAdmin = roles.includes(role.ADMIN);
  const { leUuid } = useCompanyContext();
  const queryClient = useQueryClient();
  const addCompanyDataMutation = () =>
    useMutation<CompanyResponse, Error, LegalEntityCreateSchema>({
      mutationFn: async (data) => await CompanyApiClient.addCompanyInfo(data)
    });

  const useJoinLegalEntity = () =>
    useMutation<CheckJoinLegalEntityResponse, Error, JoinLegalEntityRequest>({
      mutationFn: async (data) => {
        const response = await CompanyApiClient.joinLegalEntityRequest(data);
        return response.data;
      }
    });

  const useGetLinkInfo = () =>
    useMutation<LegalEntityLinkDto, Error, GetLinkInfoRequest>({
      mutationFn: async (data) => {
        const response = await CompanyApiClient.getLinkInfo(data);
        return response.data;
      }
    });
  const useCheckJoinLegalEntity = () =>
    useMutation<CheckJoinLegalEntityResponse, Error, string>({
      mutationFn: async (uuid: string) => {
        const response = await CompanyApiClient.checkJoinLegalEntityRequest(uuid);
        return response.data;
      }
    });

  const fetchCompanyDetectedInfoRcs = () =>
    useMutation<StandardResponse<rcsResponseType>, Error, rcsGetPayloadRequest>({
      mutationFn: async (data) => await CompanyApiClient.addCompanyDetectedRcsInfo(data)
    });

  const completeCompanyDataMutation = () =>
    useMutation<{ success: boolean }, Error, string>({
      mutationFn: async (uuidLe: string) => await CompanyApiClient.completeCompanyAdd(uuidLe),
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: QueryOptionsFactory.legalEntity(leUuid ?? "", isAdmin).queryKey
        })
    });
  const detectCompanyMutation = () =>
    useMutation<companyDetails | null, Error, { uuid: string }>({
      mutationFn: async (data) => await CompanyApiClient.fetchCompanyDetails(data.uuid)
    });
  const addRepresentativeDataMutation = () =>
    useMutation<
      StandardResponse<{
        linkUuid: string;
        legalEntityUuid: string;
      }>,
      Error,
      CompanyRelatedPersonDtoRequest
    >({
      mutationFn: async (data) =>
        await CompanyApiClient.addCompanyRepresentativeInfo(
          data.companyRelatedPersonDto,
          data.companyUuid
        )
    });
  const editRepresentativeDataMutation = () =>
    useMutation<{ success: boolean }, Error, CompanyRelatedPersonDtoRequest>({
      mutationFn: async (data) => {
        return await CompanyApiClient.editCompanyRepresentativeInfo(
          data.companyRelatedPersonDto,
          data.companyUuid,
          data.companyRelatedPersonDto.linkUuid
        );
      }
    });
  const editCompanyDataMutation = () => {
    return useMutation<
      {
        message: string;
        UUID: string;
      },
      Error,
      companyDetails & { uuid: string }
    >({
      mutationFn: async (data) => {
        const { uuid, ...payload } = data;
        return await CompanyApiClient.editCompanyForm(payload, uuid);
      },
      onSuccess: (
        _data: {
          message: string;
          UUID: string;
        },
        _body: companyDetails & { uuid: string }
      ) => {
        queryClient.invalidateQueries({
          queryKey: QueryOptionsFactory.legalEntity(leUuid ?? "", isAdmin).queryKey
        });
      }
    });
  };

  const partialEditCompanyMutation = () => {
    return useMutation<StandardResponse<DeclarativeCompanyResponse>, Error, DeclarativeCompany>({
      mutationFn: async (data) => {
        return await CompanyApiClient.partialEditCompany(data);
      },
      onSettled: () => {
        const queryKey = QueryOptionsFactory.legalEntity(leUuid ?? "", isAdmin).queryKey;

        queryClient.invalidateQueries({
          queryKey: queryKey
        });
      }
    });
  };

  const sendEmailCompanyDataMutation = () =>
    useMutation<StandardResponse<boolean>, Error, emailInvitSchema>({
      mutationFn: async (data) => await CompanyApiClient.sendEmailCompany(data)
    });

  const companiesQuery = () =>
    useQuery<LegalEntityFrontDto[], null, LegalEntityFrontDto[]>({
      ...QueryOptionsFactory.legalEntities(),
      queryFn: async () => {
        try {
          const data = await CompanyApiClient.fetchListOfCompanies();
          if (!data.success) throw new Error();
          return data.data;
        } catch (_) {
          throw new Error("Error get companies");
        }
      }
    });

  const fetchOlkypass = () =>
    useMutation<StandardResponse<boolean>, Error, string>({
      mutationFn: async (uuid: string) => await CompanyApiClient.fetchOlkypass(uuid)
    });

  const legalEntityConsentDocumentQuery = (isValidated?: boolean) =>
    useQuery<string | null, Error>({
      queryKey: ["company", "consentdocument", leUuid],
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      // staleTime: 10 * 60 * 1000,
      queryFn: leUuid
        ? async () => {
            try {
              const consentDocument = await CompanyApiClient.getConsentDocument(leUuid);
              if (!consentDocument.success) {
                throw new Error("error");
              }
              return consentDocument.data;
            } catch (_) {
              throw new Error("error");
            }
          }
        : skipToken,
      enabled: !!isValidated
    });
  const companyDetailQuery = (uuid: string | null) =>
    useQuery<companyDetails | null, null, companyDetails | null>({
      ...QueryOptionsFactory.legalEntity(uuid ?? ""),
      queryFn: uuid
        ? async () => {
            try {
              return await CompanyApiClient.fetchCompanyDetails(uuid);
            } catch (_) {
              throw new Error("error");
            }
          }
        : skipToken,
      enabled: !!uuid && !isAdmin
    });
  const companyDetailQueryAdmin = (uuid: string | null) =>
    useQuery<companyDetails | null, null, companyDetails | null>({
      ...QueryOptionsFactory.legalEntity(uuid ?? "", true),
      queryFn: uuid
        ? async () => {
            try {
              return await CompanyApiClient.fetchCompanyDetailsByAdmin(uuid);
            } catch (_) {
              throw new Error("error");
            }
          }
        : skipToken,
      enabled: !!uuid && isAdmin
    });
  const deleteCompanyLinkMutation = () =>
    useMutation<StandardResponse<null>, Error, { companyUuid: string; linkUuid: string }>({
      mutationFn: async (data) =>
        await CompanyApiClient.deleteLink(data.companyUuid, data.linkUuid),
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: QueryOptionsFactory.legalEntities().queryKey
        })
    });

  const uploadDocumentIdentityMutation = (
    options?: UseMutationOptions<
      StandardResponse<UploadOlkypassLvl2Response>,
      Error,
      ProVerifiedUploadDocumentBody
    >
  ) =>
    useMutation<StandardResponse<UploadOlkypassLvl2Response>, Error, ProVerifiedUploadDocumentBody>(
      {
        mutationFn: async (data) => CompanyApiClient.uploadDocumentIdentity(data),
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: QueryOptionsFactory.legalEntity(leUuid ?? "", isAdmin).queryKey
          }),
        ...options
      }
    );

  const editIdentity3IdentityMutation = (
    options?: UseMutationOptions<
      StandardResponse<null>,
      Error,
      { data: authenticatedCompanyFormType; id: string }
    >
  ) =>
    useMutation<StandardResponse<null>, Error, { data: authenticatedCompanyFormType; id: string }>({
      mutationFn: async (data) => await CompanyApiClient.editLevel3Form(data.data, data.id),
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: QueryOptionsFactory.legalEntity(leUuid ?? "", isAdmin).queryKey
        });
      },
      ...options
    });

  const createLegalEntityConsentDocumentsMutation = () =>
    useMutation<StandardResponse<null>, Error, string>({
      mutationFn: async (data) => await CompanyApiClient.createConsentDocument(data)
    });
  const signLegalEntityConsentDocumentMutation = () =>
    useMutation<
      StandardResponse<null>,
      Error,
      {
        status: DocumentAction;
        comment: string | null;
      }
    >({
      mutationFn: async ({ status, comment }) =>
        await CompanyApiClient.signConsentDocument({
          status,
          comment,
          idCompany: leUuid
        })
    });

  const initCardTransactionMutation = (
    options?: UseMutationOptions<InitCardTransactionResponse, Error, InitCardTransactionRequest>
  ) =>
    useMutation<InitCardTransactionResponse, Error, InitCardTransactionRequest>({
      mutationFn: async (dto) => {
        return await CompanyApiClient.initCardTransaction(leUuid ?? "", dto.lang);
      },
      ...options
    });

  const companyPaymentInformationsQuery = (enabled: boolean) =>
    useQuery<CompanyPaymentInformations, ApiError, CompanyPaymentInformations>({
      ...QueryOptionsFactory.paymentInformations(leUuid ?? ""),
      queryFn: async () => {
        return await CompanyApiClient.fetchCompanyPaymentInformations(leUuid ?? "");
      },
      enabled: enabled
    });

  const initCompanyPaymentMutation = (
    options?: UseMutationOptions<CompanyPaymentInformations, Error, string>
  ) =>
    useMutation<CompanyPaymentInformations, Error, string>({
      mutationFn: async (legalEntityUuid) => {
        return await CompanyApiClient.initCompanyPayment(legalEntityUuid);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryOptionsFactory.paymentInformations(leUuid ?? "").queryKey
        });

        queryClient.invalidateQueries({
          queryKey: QueryOptionsFactory.legalEntity(leUuid ?? "").queryKey
        });
      },
      ...options
    });

  return {
    addCompanyDataMutation,
    detectCompanyMutation,
    editIdentity3IdentityMutation,
    uploadDocumentIdentityMutation,
    addRepresentativeDataMutation,
    companiesQuery,
    companyDetailQuery,
    deleteCompanyLinkMutation,
    editCompanyDataMutation,
    sendEmailCompanyDataMutation,
    completeCompanyDataMutation,
    editRepresentativeDataMutation,
    fetchOlkypass,
    useCheckJoinLegalEntity,
    useJoinLegalEntity,
    fetchCompanyDetectedInfoRcs,
    companyDetailQueryAdmin,
    createLegalEntityConsentDocumentsMutation,
    legalEntityConsentDocumentQuery,
    signLegalEntityConsentDocumentMutation,
    useGetLinkInfo,
    partialEditCompanyMutation,
    initCardTransactionMutation,
    companyPaymentInformationsQuery,
    initCompanyPaymentMutation
  };
};
